import type { ReactElement, ReactNode } from 'react';
import type { PlatformOverviewDto, StorageUsageDto } from '@gr/core/platform/admin/dto';
import { Link, useT } from '../../../../app/di';
import { formatDateTime, formatFileSize, formatNumber } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import { Card } from '../../../../ui-kit/Card/Card';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { Stat, StatGrid } from '../../../../ui-kit/Stat/Stat';
import { uptimeParts } from '../model/uptime';
import styles from './PlatformOverview.module.scss';

/** Фактический объём хранилища: замер идёт отдельно и может не удаться (ADM-UC-1.CC-1). */
export interface StorageMeasurement {
  usage: StorageUsageDto | null;
  loading: boolean;
  /** Коды ошибки замера. */
  error: readonly string[] | null;
  onMeasure(): void;
}

export interface PlatformOverviewProps {
  overview: PlatformOverviewDto;
  storage: StorageMeasurement;
}

/** ADM-UC-1: состояние платформы — показатели, сервер, база и хранилище; блоги — в «Все блоги» (ADM-UC-2). */
export function PlatformOverview({ overview, storage }: PlatformOverviewProps): ReactElement {
  const { t, te, locale } = useT();
  const size = (bytes: number): string => formatFileSize(bytes, locale);
  const n = (value: number): string => formatNumber(value, locale);
  const { users, totals, server, database } = overview;
  const mode = server.demo ? t('admin.overview.server.modeDemo') : server.testMode ? t('admin.overview.server.modeTest') : t('admin.overview.server.modeNormal');

  return (
    <div className="u-stack-lg">
      <StatGrid>
        <Stat
          label={t('admin.overview.stats.users')}
          value={n(users.total)}
          hint={t('admin.overview.stats.usersHint', { admins: users.admins, disabled: users.disabled, active: users.withActiveSession })}
        />
        <Stat
          label={t('admin.overview.stats.blogs')}
          value={n(totals.blogs)}
          hint={
            <Link to="adminBlogs" params={{}}>
              {t('admin.overview.stats.allBlogs')}
            </Link>
          }
        />
        <Stat label={t('admin.overview.stats.posts')} value={n(totals.posts)} />
        <Stat label={t('admin.overview.stats.entities')} value={n(totals.entities)} hint={t('admin.overview.stats.entitiesHint', { types: totals.entityTypes })} />
        <Stat label={t('admin.overview.stats.media')} value={n(totals.media)} hint={size(totals.mediaBytes)} />
        <Stat
          label={t('admin.overview.stats.publications')}
          value={n(totals.publications.published)}
          hint={t('admin.overview.stats.publicationsHint', { planned: totals.publications.planned, due: totals.publications.due, published: totals.publications.published })}
        />
        <Stat label={t('admin.overview.stats.traffic')} value={size(totals.trafficBytesThisMonth)} hint={overview.month} />
      </StatGrid>

      <div className={styles.columns}>
        <Card title={t('admin.overview.server.title')}>
          <Facts
            items={[
              [t('admin.overview.server.version'), server.version],
              [t('admin.overview.server.uptime'), t('admin.overview.server.uptimeValue', uptimeParts(server.uptimeSeconds))],
              [t('admin.overview.server.startedAt'), formatDateTime(server.startedAt, locale)],
              [t('admin.overview.server.instance'), server.instanceId],
              [t('admin.overview.server.database'), `${server.databaseDriver} · ${server.databaseClient}`],
              [t('admin.overview.server.storage'), server.storageDriver],
              [t('admin.overview.server.coordination'), server.coordination],
              [t('admin.overview.server.mediaProcessor'), server.mediaProcessor],
              [t('admin.overview.server.mode'), mode],
              [t('admin.overview.server.trafficLimit'), server.mediaTrafficLimitBytes === null ? t('admin.overview.server.noLimit') : size(server.mediaTrafficLimitBytes)],
              [
                t('admin.overview.server.jobs'),
                server.jobLeaders.length === 0 ? (
                  t('admin.overview.server.noJobs')
                ) : (
                  <ul className="u-list-reset">
                    {server.jobLeaders.map((leader) => (
                      <li key={leader.job}>
                        {leader.job} — {leader.instance}
                      </li>
                    ))}
                  </ul>
                ),
              ],
            ]}
          />
        </Card>
        <div className="u-stack-lg">
          <Card title={t('admin.overview.storage.title')}>
            <div className="u-stack">
              <Facts
                items={[
                  [t('admin.overview.storage.accounted'), size(totals.mediaBytes)],
                  [
                    t('admin.overview.storage.actual'),
                    storage.loading ? (
                      <span className="u-muted">{t('admin.overview.storage.measuring')}</span>
                    ) : storage.usage ? (
                      <>
                        {size(storage.usage.bytes)}
                        <div className="u-muted u-small">{t('admin.overview.storage.actualHint', { objects: storage.usage.objects })}</div>
                      </>
                    ) : null,
                  ],
                ]}
              />
              {storage.error ? <Alert tone="warning">{te(storage.error)}</Alert> : null}
              <div>
                <Button size="sm" icon="search" loading={storage.loading} onClick={storage.onMeasure}>
                  {t('admin.overview.storage.measure')}
                </Button>
              </div>
            </div>
          </Card>
          <Card title={t('admin.overview.database.title')}>
            <div className="u-stack">
              <Facts items={[[t('admin.overview.database.size'), database.sizeBytes === null ? t('admin.overview.database.unknown') : size(database.sizeBytes)]]} />
              <DataTable
                label={t('admin.overview.database.title')}
                rows={database.tables}
                rowKey={(row) => row.table}
                columns={[
                  { key: 'table', header: t('admin.overview.database.table'), primary: true, cell: (row) => row.table },
                  { key: 'rows', header: t('admin.overview.database.rows'), align: 'end', cell: (row) => n(row.rows) },
                ]}
              />
            </div>
          </Card>
        </div>
      </div>

    </div>
  );
}

/** Пары «подпись — значение»: на телефоне друг под другом, шире — в две колонки. */
function Facts({ items }: { items: readonly (readonly [string, ReactNode])[] }): ReactElement {
  return (
    <dl className={styles.facts}>
      {items.map(([term, value]) => (
        <div key={term} className={styles.fact}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
